import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Edit2, Trash2, Phone, Mail, MapPin, Stethoscope, Scissors, AlertTriangle, User } from 'lucide-react';

const ROLE_CONFIG = {
  vet: { icon: Stethoscope, color: 'bg-[#E9EFE8] text-[#2D6A4F]', label: 'Veterinarian' },
  farrier: { icon: Scissors, color: 'bg-[#F9F7F3] text-[#8B6914]', label: 'Farrier' },
  emergency: { icon: AlertTriangle, color: 'bg-[#FEF3C7] text-[#92400E]', label: 'Emergency' },
  other: { icon: User, color: 'bg-[#E0E7FF] text-[#3730A3]', label: 'Other' },
};

export default function EmergencyPage() {
  const { authHeaders, API, hasFullAccess } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [form, setForm] = useState({ name: '', role: 'vet', phone: '', email: '', address: '', notes: '' });

  const fetchContacts = async () => {
    try {
      const res = await axios.get(`${API}/emergency-contacts`, authHeaders());
      setContacts(res.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchContacts(); }, []); // eslint-disable-line

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editContact) {
        await axios.put(`${API}/emergency-contacts/${editContact.id}`, form, authHeaders());
        toast.success('Contact updated!');
      } else {
        await axios.post(`${API}/emergency-contacts`, form, authHeaders());
        toast.success('Contact added!');
      }
      setShowDialog(false); setEditContact(null);
      setForm({ name: '', role: 'vet', phone: '', email: '', address: '', notes: '' });
      fetchContacts();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`${API}/emergency-contacts/${id}`, authHeaders());
      toast.success('Contact deleted');
      fetchContacts();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const openAdd = () => {
    if (!hasFullAccess) { toast.error('Subscribe to add contacts.'); return; }
    setEditContact(null);
    setForm({ name: '', role: 'vet', phone: '', email: '', address: '', notes: '' });
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditContact(c);
    setForm({ name: c.name, role: c.role || 'vet', phone: c.phone || '', email: c.email || '', address: c.address || '', notes: c.notes || '' });
    setShowDialog(true);
  };

  const grouped = {
    emergency: contacts.filter(c => c.role === 'emergency'),
    vet: contacts.filter(c => c.role === 'vet'),
    farrier: contacts.filter(c => c.role === 'farrier'),
    other: contacts.filter(c => !['emergency', 'vet', 'farrier'].includes(c.role)),
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 animate-fade-in">
          <div>
            <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="emergency-title">
              Emergency Contacts
            </h1>
            <p className="text-[#4B6B58] mt-1">Quick access to your vet, farrier, and emergency contacts.</p>
          </div>
          <button onClick={openAdd} data-testid="add-contact-button" className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]">
            <Plus size={18} /> Add Contact
          </button>
        </div>

        {loading ? (
          <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="bg-white rounded-[2rem] h-24 animate-pulse" />)}</div>
        ) : contacts.length === 0 ? (
          <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]" data-testid="no-contacts">
            <AlertTriangle size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
            <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">No Emergency Contacts</h3>
            <p className="text-sm text-[#4B6B58] mb-4">Add your vet, farrier, and emergency contacts for quick access.</p>
            <button onClick={openAdd} data-testid="add-first-contact" className="px-6 py-2.5 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">Add First Contact</button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([role, group]) => {
              if (group.length === 0) return null;
              const config = ROLE_CONFIG[role] || ROLE_CONFIG.other;
              return (
                <div key={role} className="animate-fade-in">
                  <h2 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-3 flex items-center gap-2">
                    <config.icon size={18} /> {config.label}s
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {group.map(contact => (
                      <div key={contact.id} className="bg-white rounded-[1.5rem] p-5 card-hover shadow-[0_4px_12px_rgb(27,67,50,0.04)]" data-testid={`contact-${contact.id}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${config.color} mb-2`}>
                              <config.icon size={12} /> {config.label}
                            </span>
                            <h3 className="font-semibold text-[#122A1F]">{contact.name}</h3>
                          </div>
                          {hasFullAccess && (
                            <div className="flex gap-1">
                              <button onClick={() => openEdit(contact)} data-testid={`edit-contact-${contact.id}`} className="p-1.5 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Edit2 size={14} /></button>
                              <button onClick={() => handleDelete(contact.id)} data-testid={`delete-contact-${contact.id}`} className="p-1.5 rounded-full hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
                            </div>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-2 text-sm text-[#2D6A4F] hover:underline" data-testid={`call-${contact.id}`}>
                              <Phone size={14} /> {contact.phone}
                            </a>
                          )}
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-[#4B6B58] hover:underline">
                              <Mail size={14} /> {contact.email}
                            </a>
                          )}
                          {contact.address && (
                            <p className="flex items-center gap-2 text-sm text-[#4B6B58]"><MapPin size={14} /> {contact.address}</p>
                          )}
                          {contact.notes && <p className="text-xs text-[#4B6B58] mt-2 italic">{contact.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-md" data-testid="contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">{editContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription className="text-[#4B6B58]">Emergency and care provider contacts.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Name *</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="contact-name-input" className="stable-input w-full" /></div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Role</label>
              <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} data-testid="contact-role-select" className="stable-input w-full">
                <option value="vet">Veterinarian</option><option value="farrier">Farrier</option><option value="emergency">Emergency</option><option value="other">Other</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Phone</label><input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} data-testid="contact-phone-input" className="stable-input w-full" type="tel" /></div>
              <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Email</label><input value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="contact-email-input" className="stable-input w-full" type="email" /></div>
            </div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Address</label><input value={form.address} onChange={e => setForm({...form, address: e.target.value})} data-testid="contact-address-input" className="stable-input w-full" /></div>
            <div><label className="block text-sm font-medium text-[#122A1F] mb-1">Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="contact-notes-input" className="stable-input w-full" rows={2} /></div>
            <button type="submit" data-testid="contact-save-button" className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">{editContact ? 'Update' : 'Add Contact'}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
